import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useLiveRoomSocket } from "@/hooks/useLiveRoomWS";
import { toast } from "sonner";
import { Loader2, Check, X, UserPlus } from "lucide-react";

/**
 * LiveRoomCohostBanner — shows an inline Accept / Decline strip when the
 * current viewer has a pending co-host invite for the stream they're
 * watching. Removes the need to leave the live room and hunt for the
 * invite on the Notifications page.
 *
 * Polls once on mount and then every 20s so an invite fired from the
 * host mid-stream shows up here as well. Invisible when there's no
 * pending invite.
 */
export default function LiveRoomCohostBanner({ streamId, onAccepted }) {
  const [invite, setInvite] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/live/${streamId}/cohost/my-invite`);
      setInvite(data?.invite || null);
    } catch {
      /* silent — the banner just stays hidden */
    }
  }, [streamId]);

  // One load on mount; a `cohost:invite` frame on the shared room socket
  // (or a reconnect `sync`) reloads it. No polling.
  const socket = useLiveRoomSocket();
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const offs = [
      socket.subscribe("cohost:invite", load),
      socket.subscribe("cohost:accept", load),
      socket.subscribe("cohost:decline", load),
      socket.subscribe("sync", load),
    ];
    return () => offs.forEach((off) => off());
  }, [socket, load]);

  if (!invite) return null;

  const respond = async (accept) => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post(`/live/cohost/invites/${invite.id}/respond`, { accept });
      if (accept) {
        toast.success("You're on stage! Grabbing your co-host feed…");
        onAccepted?.();
      } else {
        toast("Invite declined.");
      }
      setInvite(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not respond.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="cohost-invite-banner"
      className="absolute top-28 inset-x-3 z-30 bg-gradient-to-r from-rose-600/95 to-rose-500/95 border border-white/20 rounded-lg shadow-2xl backdrop-blur-md px-4 py-3 flex items-center gap-3"
    >
      <div className="w-8 h-8 rounded-full bg-white/15 flex items-center justify-center shrink-0">
        <UserPlus size={16} className="text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs uppercase tracking-widest text-white/70">Co-host invite</div>
        <div className="text-sm text-white font-semibold">
          The host wants you on stage. Accept?
        </div>
      </div>
      <button
        type="button"
        onClick={() => respond(false)}
        disabled={busy}
        data-testid="cohost-invite-decline"
        className="w-8 h-8 rounded-full bg-black/30 hover:bg-black/50 disabled:opacity-50 text-white flex items-center justify-center"
        aria-label="Decline"
        title="Decline"
      >
        <X size={14} />
      </button>
      <button
        type="button"
        onClick={() => respond(true)}
        disabled={busy}
        data-testid="cohost-invite-accept"
        className="bg-white text-rose-600 hover:bg-rose-50 disabled:opacity-60 font-semibold text-xs rounded-full px-4 py-1.5 flex items-center gap-1"
      >
        {busy ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Accept
      </button>
    </div>
  );
}
